import { ApiError } from '@core/errors/api-error';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';

export type Extra = string | number | boolean | null;

/** Grouping overrides for a single Sentry event. */
export interface LogGrouping {
  fingerprint?: string[];
  tags?: Record<string, string>;
}

const guidRegExp = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Normalizes an API URL for Sentry grouping: drops the query string and fragment and replaces
 * GUID and numeric path segments, so requests for different IDs group into one issue.
 */
export function normalizeApiUrl(url: string): string {
  const [path] = url.split(/[?#]/, 1);
  return path
    .split('/')
    .map(segment => {
      if (guidRegExp.test(segment)) {
        return '{guid}';
      }
      return /^\d+$/.test(segment) ? '{number}' : segment;
    })
    .join('/');
}

export class Logger {
  unknownError(error: unknown, extra?: Record<string, Extra>) {
    this.log('error', this.unknownToError(error), extra);
  }

  unknownWarn(error: unknown, extra?: Record<string, Extra>) {
    this.log('warning', this.unknownToError(error), extra);
  }

  error(messageOrError: string | Error, extra?: Record<string, Extra>, grouping?: LogGrouping) {
    this.log('error', messageOrError, extra, grouping);
  }

  warn(messageOrError: string | Error, extra?: Record<string, Extra>, grouping?: LogGrouping) {
    this.log('warning', messageOrError, extra, grouping);
  }

  log(
    severity: Sentry.SeverityLevel,
    messageOrError: string | Error,
    extra?: Record<string, Extra>,
    grouping?: LogGrouping
  ) {
    if (environment.logToConsole) {
      this.logToConsole(severity, messageOrError, extra);
    }

    if (environment.sentry) {
      this.logToSentry(severity, messageOrError, extra, grouping);
    }
  }

  private logToSentry(
    severity: Sentry.SeverityLevel,
    messageOrError: string | Error,
    extra?: Record<string, Extra>,
    grouping?: LogGrouping
  ) {
    Sentry.withScope(scope => {
      scope.setLevel(severity);

      if (messageOrError instanceof ApiError) {
        this.extractApiErrorDetails(scope, messageOrError);
      }

      if (extra) {
        scope.setExtras(extra);
      }

      if (grouping?.fingerprint) {
        scope.setFingerprint(grouping.fingerprint);
      }

      if (grouping?.tags) {
        scope.setTags(grouping.tags);
      }

      if (messageOrError instanceof Error) {
        Sentry.captureException(messageOrError);
      } else {
        Sentry.captureMessage(messageOrError);
      }
    });
  }

  private logToConsole(
    severity: Sentry.SeverityLevel,
    messageOrError: string | Error,
    extra?: Record<string, unknown>
  ) {
    const args = extra !== undefined ? [messageOrError, extra] : [messageOrError];

    switch (severity) {
      case 'fatal':
      case 'error':
        console.error(...args);
        break;
      case 'warning':
        console.warn(...args);
        break;
      case 'info':
        console.info(...args);
        break;
      case 'debug':
        console.debug(...args);
        break;
    }
  }

  private extractApiErrorDetails(scope: Sentry.Scope, error: ApiError) {
    const url = normalizeApiUrl(error.url ?? '');
    scope.setFingerprint(['xhr', String(error.status), url]);
    scope.setTag('xhr.status', error.status);
    scope.setTag('xhr.url', url);
  }

  private unknownToError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    const newError = new Error('An unknown error occurred');
    newError.cause = error;
    return newError;
  }
}

export const logging = new Logger();
